/**
 * Date and Time Utilities
 * 
 * Functions for formatting and manipulating dates and times
 */

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import i18n from "@/i18n";

dayjs.extend(relativeTime);

/**
 * Clean date string to YYYY-MM-DD format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Cleaned date string in YYYY-MM-DD format
 */
export function cleanDate(dateString) {
  if (!dateString) return "";

  // Si c'est un objet Date, le convertir en string ISO
  if (dateString instanceof Date) {
    return dateString.toISOString().split("T")[0];
  }

  // Si c'est un objet vide ou autre type non-string, retourner ""
  if (typeof dateString !== "string") {
    return "";
  }

  return dateString.split("T")[0];
}

/**
 * Format time string to HH:MM format
 * @param {string} time - Time string (e.g., "14:30:00")
 * @returns {string} Formatted time (e.g., "14:30")
 */
export function formatTime(time) {
  if (!time) return "";

  const [hours, minutes] = time.split(":");

  return `${hours}:${minutes}`;
}

/**
 * Get formatted current date and time
 * @returns {string} Formatted date and time (e.g., "2024/01/15 - 14:30")
 */
export function formattedDateTime() {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}/${month}/${day} - ${hours}:${minutes}`;
}

/**
 * Format date to YYYY-MM-DD format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string
 */
export function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toISOString().split("T")[0];
}

/**
 * Get human-readable time ago (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Human-readable time ago string
 */
export function timeAgo(date) {
  if (!date) return '';

  // Obtenir la locale actuelle depuis i18n
  const currentLocale = i18n.global.locale.value;

  // Configurer dayjs avec la locale actuelle pour les traductions
  dayjs.locale(currentLocale);

  // fromNow() utilisera automatiquement la locale configurée
  // Day.js gère automatiquement le format RTL pour l'arabe
  return dayjs(date).fromNow();
}

/**
 * Format date to human-readable format (e.g., "Monday, January 15, 2024")
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Human-readable date string
 */
export function formatHumanizedDate(dateInput) {
  const userLocale = localStorage.getItem("user_language") || "en";
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput || Date.now());

  return date.toLocaleDateString(userLocale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format birth date for display (e.g., "March 22, 2003")
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @returns {string} Formatted birth date
 */
export function formatBirthDate(birthDate) {
  if (!birthDate) return '';
  
  const currentLocale = i18n.global.locale.value;
  dayjs.locale(currentLocale);
  
  const date = dayjs(birthDate);
  return date.format('MMMM D, YYYY');
}

/**
 * Get recent birthday information
 * Calculates how many days ago the birthday occurred this year
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @returns {Object} Object with daysAgo property
 */
export function getRecentBirthdayInfo(birthDate) {
  if (!birthDate) return { daysAgo: null };
  
  const today = dayjs();
  const birthday = dayjs(birthDate);
  const thisYearBirthday = birthday.year(today.year());
  
  let daysAgo = today.diff(thisYearBirthday, 'day');
  
  // Si l'anniversaire de cette année est dans le futur, utiliser l'année précédente
  if (daysAgo < 0) {
    daysAgo = today.diff(thisYearBirthday.subtract(1, 'year'), 'day');
  }
  
  return {
    daysAgo: Math.abs(daysAgo)
  };
}

/**
 * Get the age someone is turning this year
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @returns {number} Age the person is turning this year
 */
export function getAgeTurning(birthDate) {
  if (!birthDate) return 0;
  
  const today = dayjs();
  const birthday = dayjs(birthDate);
  
  // Calculate current age
  let age = today.year() - birthday.year();
  
  // Check if birthday hasn't occurred yet this year
  const thisYearBirthday = birthday.year(today.year());
  if (today.isBefore(thisYearBirthday)) {
    // Birthday hasn't happened yet, so they're turning this age
    return age;
  }
  
  // Birthday has passed, return the age they turned
  return age;
}